"use client"
import {
    faAngleDown,
    faAngleUp,
    faCaretDown,
    faCaretUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";

export interface OptionType {
    label: string;
    value: string;
}

export interface FilterDropdownProps {
    options: OptionType[];
    name: string;
    selectedOption?: OptionType;  // Optional - only used if no URL param is found
    placeholder?: string;
    searchable?: boolean;
    className?: string;
    loading?: boolean;
    onChange?: (value: string) => void;
}

export default function FilterDropdown({
    options,
    name,
    selectedOption,
    placeholder = "Select an option",
    searchable = false,
    className = "",
    loading = false,
    onChange,
}: FilterDropdownProps) {
    const searchParams = useSearchParams();
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLLIElement | null>>([]);
    
    // Get URL parameter value for this filter
    const urlParamValue = searchParams.get(name);
    
    // Initialize currentSelection from URL or fallback to provided selectedOption or first option
    const [currentSelection, setCurrentSelection] = useState<OptionType | undefined>(() => {
        // First try to get from URL
        if (urlParamValue && options.length > 0) {
            const fromUrl = options.find(option => option.value === urlParamValue);
            if (fromUrl) return fromUrl;
        }
        
        // Then try the provided selectedOption
        if (selectedOption) return selectedOption;
        
        // Finally default to first option if available
        return options.length > 0 ? options[0] : undefined;
    });

    // Update selection when options change or URL parameters change
    useEffect(() => {
        if (options.length === 0) return;
        
        // Priority 1: URL parameter
        if (urlParamValue) {
            const matchingOption = options.find(option => option.value === urlParamValue);
            if (matchingOption && (currentSelection?.value !== matchingOption.value)) {
                setCurrentSelection(matchingOption);
                return;
            }
        }
        
        // Priority 2: Provided selectedOption (only if no URL param)
        if (!urlParamValue && selectedOption && currentSelection?.value !== selectedOption.value) {
            setCurrentSelection(selectedOption);
            return;
        }
        
        // Priority 3: First option (only if nothing else matches and we don't have a selection)
        if (!currentSelection && options.length > 0) {
            setCurrentSelection(options[0]);
        }
    }, [options, urlParamValue, selectedOption, currentSelection]);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleItemClick = (option: OptionType) => {
        setCurrentSelection(option);
        setIsDropdownOpen(false);
        setSearchTerm("");
        
        // If onChange handler is provided, use it instead of updating URL
        if (onChange) {
            onChange(option.value);
            return;
        }
        
        // Otherwise, update URL params as before
        const params = new URLSearchParams(searchParams.toString());
        if (option.value) {
            params.set(name, option.value);
        } else {
            params.delete(name); // Remove the query param if the selected option has no value
        }
        
        // Navigate to the new URL which will refresh the page
        window.location.href = `${window.location.pathname}?${params.toString()}`;
    };

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (!(e.target as Element).closest(".filter-dropdown")) {
                setIsDropdownOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener("click", handleOutsideClick);
        return () => document.removeEventListener("click", handleOutsideClick);
    }, []);

    useEffect(() => {
        if (isDropdownOpen && searchable) {
            searchInputRef.current?.focus();
        }
    }, [isDropdownOpen, searchable]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedItemIndex((prevIndex) => {
                const newIndex = prevIndex + 1;
                return newIndex < filteredOptions.length ? newIndex : 0;
            });
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedItemIndex((prevIndex) => {
                const newIndex = prevIndex - 1;
                return newIndex >= 0 ? newIndex : filteredOptions.length - 1;
            });
        } else if (e.key === "Enter") {
            if (selectedItemIndex !== -1) {
                handleItemClick(filteredOptions[selectedItemIndex]);
            }
        } else if (e.key === "Escape") {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        if (selectedItemIndex !== -1 && itemRefs.current[selectedItemIndex]) {
            const selectedItem = itemRefs.current[selectedItemIndex] as HTMLElement;
            const list = listRef.current;
            if (list && selectedItem) {
                const selectedItemRect = selectedItem.getBoundingClientRect();
                const listRect = list.getBoundingClientRect();
                if (
                    selectedItemRect.top < listRect.top ||
                    selectedItemRect.bottom > listRect.bottom
                ) {
                    selectedItem.scrollIntoView({ block: "center" });
                }
            }
        }
    }, [selectedItemIndex]);

    return (
        <div className={`relative filter-dropdown ${className}`} onKeyDown={handleKeyDown}>
            <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center h-full justify-between gap-2 cursor-pointer"
            >
                <p className="line-clamp-1">{currentSelection?.label || placeholder}</p>
                <FontAwesomeIcon
                    className={isDropdownOpen ? "" : "mb-1"}
                    icon={isDropdownOpen ? faCaretUp : faCaretDown}
                />
            </div>
            {isDropdownOpen && (
                <div
                    className="absolute left-0 top-[105%] max-h-[200px] z-40 overflow-auto w-full px-1 mt-1 bg-white shadow-lg rounded-md border border-gray-500 dark:bg-darkbg"
                    ref={listRef}
                >
                    {searchable && (
                        <div className="sticky top-0 bg-white dark:bg-darkbg">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                className="border-b border-gray-500 py-2 px-4 w-full dark:bg-darkbg dark:text-white"
                                ref={searchInputRef}
                            />
                        </div>
                    )}
                    {loading ? (
                        <div className="py-4 text-center">
                            <Spinner />
                        </div>
                    ) : (
                        <ul>
                            {filteredOptions.map((option, index) => (
                                <li
                                    key={option.value}
                                    onClick={() => handleItemClick(option)}
                                    className={`px-4 dark:text-white py-2 cursor-pointer rounded hover:bg-gray-200 dark:hover:bg-darkCard ${
                                        selectedItemIndex === index
                                            ? "bg-gray-200 dark:bg-darkCard"
                                            : ""
                                    }`}
                                    tabIndex={0}
                                    ref={(el) => {
                                        itemRefs.current[index] = el;
                                    }}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

// Simple spinner component for loading state
// This can be replaced with the project's standard spinner component
function Spinner() {
    return (
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" 
             role="status">
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
            </span>
        </div>
    );
} 